/* eslint-disable max-lines */
import { IGetOfferDataResponseSchema } from '@cian/api-models/offer-card/v1/get-offer-data';
import { IHttpApi } from '@cian/http-api';
import { IConfig } from '@cian/config/shared';
import { ILogger } from '@cian/logger/shared';
import { ITelemetryLogger } from '@cian/telemetry/shared';
import { IPIKBookingWidgetContext } from '@cian/pik-booking-widget';
import {
  AnyAction,
  applyMiddleware,
  combineReducers,
  compose,
  createStore as _createStore,
  Reducer,
  Store,
} from 'redux';
// tslint:disable-next-line
import thunkMiddleware, { ThunkAction } from 'redux-thunk';
import { IExperiment } from '../api/experiments';
import { IAbUseExperiment } from '../types/abUseExperiments';
import { IOfferData } from '../types/offerData';
import { feedbackReducer, IFeedback } from '../FeedbackExperiment/reducers/feedback';
import { stalkerBanner } from '../registrationTriggers/StalkerBanner/reducers';
import { ICountrysideBuilderLandingPromoData, INewbuildingLabel, IRequestDemandState } from '../types';
import { IMakeRequest } from '../utils/make_request';
import { BtiDataErrorPopupReducer, IBtiDataErrorPopupStoreState } from './bti_error_data_popup';
import { commentsReducer, IComments } from './comments';
import { complaintReducer, IComplaintState } from './complaint';
import { contractRentSaleReducer, IContractRentSaleState } from './contract_rent_sale';
import { experimentsReducer } from './experiments';
import { abUseExperimentsReducer } from './abUseExperiments';
import { favoriteMotivationReducer, IFavoriteMotivationState } from './favorite_motivation';
import { galleryReducer, IGalleryData } from './gallery/';
import { homeownerReducer } from './homeowner';
import { ISimilarOffersData, IViewedData, markViewedReducer, mapSimilarReducer } from './map_similar';
import { IMapTabs, mapTabsReducer } from './map_tabs';
import { IMortgageMeta, mortgageMetaReducer } from './mortgage';
import { asideReducer, IAside } from './navigation';
import { INewbuildingMvkState, newbuildingMvkReducer } from './newbuilding_mvk';
import { newbuildingChatReducer } from './newbuildingChat';
import { offerReducer } from './offer';
import { chatReducer, IOfferChatsState } from './offer_chats/index';
import { IOfferStatsPopupState, offerStatsPopupReducer } from './offer_stats_popup';
import { phoneCollapseReducer } from './phone_collapse';
import { IPopupCallback, popupCallbackReducer } from './popups/callback';
import { ILegendState, legendModalReducer } from './popups/legend';
import { IPrintModal, printModalReducer } from './popups/print';
import { IReclaimModalState, reclaimModalReducer } from './popups/reclaim';
import { IRemoveState, removeModalReducer } from './popups/remove';
import { ICommercialSurveyWidgetModalState, commercialSurveyWidgetModal } from './popups/commecial_survey';
import { subscriptionAddressReducer, ISubscriptionAddressState } from './popups/subscription_address';
import { pushSubscribedReducer, TPushSubscribed } from './push_subscription';
import { IReviewRequestData, reviewRequestReducer } from './review_request';
import { ISeoLinksState, seoLinksReducer } from './seo_links';
import { ISimilarState, similarOffersReducer } from './similar';
import { specialPromoInfoReducer, TSpecialPromoInfoState } from './special_promo_info';
import { infrastructureMapReducer, TInfrastructureMapState } from './infrastructure-map';
import {
  ISubscriptionPriceChangesPopup,
  subscriptionPriceChangesPopupReducer,
} from './subscription_price_changes_popup';
import { userDataReducer } from './user';
import { ebcReducer, IEbcState } from './ebc';
import { complaintFeedbackReducer, IFeedbackComplaintState } from './complaint/feedback_complaint';
import { hideOfferReducer, IHideOffer } from './hide_offer';
import { trackingReducer, ITrackingReducerState } from './tracking';
import { createSimpleReducer } from './helpers';
import { demandReducer, IDemandReducerState } from '@cian/demand-trap-widget';
import { IVerifyPhoneReducer, verifyPhoneReducer } from '@cian/sms-validation-widget';
import { widgetMarkupReducer } from './widgetsMarkup/reducers';
import { reducer as dealRent, IState as IDealRentState } from './deal-rent';
import { complaintPopupOpenReducer } from './complaintPopupOpen';
import { IOfferHistory, offerHistoryReducer } from './offerHistory';
import { INewbuildingChat } from '../types/newbuildingChat';
import { INewbuildingConsultant } from '../types/newbuildingConsultant';
import { requestDemandReducer } from '../reducers/requestDemand';

export interface IPageData {
  subdomain: string;
  dealType: 'sale' | 'rent';
  offerType: 'flat' | 'suburban' | 'commercial';
  offerId: number;
}

export interface IBookingState {
  isPikBookingSucceeded: boolean;
  bookedOfferId: number;
}

export interface IWidgetsMarkup {
  underMapMarkup: string;
}

export interface IState extends IBookingState {
  abGroup: number;
  abUseExperiments: IAbUseExperiment[];
  aside: IAside;
  btiErrorData: IBtiDataErrorPopupStoreState;
  captchaSiteKey: string;
  captchaSpamSiteKey: string;
  commentsData: IComments;
  complaint: IComplaintState;
  complaintPopupOpen: boolean;
  contractRentSale: IContractRentSaleState;
  demand: IDemandReducerState;
  requestDemand: IRequestDemandState;
  verifyPhone: IVerifyPhoneReducer;
  experiments: IExperiment[];
  favoriteMotivation: IFavoriteMotivationState;
  feedback: IFeedback;
  gallery: IGalleryData;
  hideOffer: IHideOffer;
  isStartChatWidgetInDropdown: boolean;
  isCountrysideOffersBuilderBadgeEnabled: boolean;
  legend: ILegendState;
  mapSimilar: ISimilarOffersData;
  mapTabs: IMapTabs;
  mortgageMeta: IMortgageMeta;
  offerChat: IOfferChatsState;
  offerData: IOfferData;
  offerStatsPopup: IOfferStatsPopupState;
  pageData: IPageData;
  phoneCollapse: boolean;
  popupCallback: IPopupCallback;
  printModal: IPrintModal;
  reclaimModal: IReclaimModalState;
  remove: IRemoveState;
  reviewRequest: IReviewRequestData;
  seoLinks: ISeoLinksState;
  serviceBaseUrl: string;
  similarOffers: ISimilarState;
  specialPromoInfo: TSpecialPromoInfoState;
  subscriptionPriceChangesPopup: ISubscriptionPriceChangesPopup;
  tracking: ITrackingReducerState;
  viewedSimilar: IViewedData;
  pushSubscribed: TPushSubscribed;
  newbuildingMvk: INewbuildingMvkState;
  newbuildingChat: INewbuildingChat;
  newbuildingConsultant: INewbuildingConsultant;
  ebcInfo: IEbcState;
  complaintFeedbackFrom: IFeedbackComplaintState;
  isStalkerBannerShown: boolean;
  /** Флаг для выкатки брэндирования */
  isBrandingAvailable: boolean;
  /** Перечень регионов ранжированного поиска */
  mlSearchRegions?: number[];
  /** Флаг активности ML-выдачи для всех */
  isMlSearchForAllEnabled: boolean;
  /** Показывать ли баннер юридической поддержки **/
  isPravocardEnabled: boolean;
  subscriptionAddress: ISubscriptionAddressState;
  /** Списком realtyId объявлений, для которых показываем зеленую плашку **/
  akkCheckPassedOfferIds: number[] | null;
  /** Флаг для demand - является ли юзер собственником **/
  isHomeowner: boolean;
  soprApi: string;
  widgetsMarkup: IWidgetsMarkup;
  countrysideBuilderLandingData?: ICountrysideBuilderLandingPromoData | null;
  infrastructureMap: TInfrastructureMapState;
  isCountrysidePromoEnabled: boolean;
  isProjectListEnabled: boolean;
  isQuizEnabled: boolean;
  /** Списк publisherUserId объявлений, публикации которых учавствуют аукционах **/
  knAuctionCompanies: number[];
  dealRent: IDealRentState;
  /** Список регионов, в которых коммунальные услуги в аренде жилой не включаются в стоимость */
  excludedUtilitiesTermsRegions: number[];
  dealRentVasCost: number;
  newbuildingLabel: INewbuildingLabel | null;
  isHomeownerDocsRequired: boolean;
  offerHistory: IOfferHistory;
  /** Показываем кнопку аналитики  */
  showAnalyticsButton: boolean;
  commercialSurvey: ICommercialSurveyWidgetModalState;
  entryToShowEnabled: boolean;
}

export type IThunkActionCreator<T = void> = ThunkAction<T, IState, IContext>;

export interface IContext {
  config: IConfig;
  httpApi: IHttpApi;
  logger: ILogger;
  // FIXME: DEPRECATED: использовать http-api
  makeRequest: IMakeRequest;
  telemetry: ITelemetryLogger;
  pikBookingContext?: IPIKBookingWidgetContext;
}

let storeReminder: Store<IState>;

export function createStore(context: IContext, preloadedState: IState, isBrowser: boolean = false): Store<IState> {
  const rootReducer = combineReducers<IState>({
    abGroup: (state: IState = preloadedState) => state,
    abUseExperiments: abUseExperimentsReducer,
    aside: asideReducer,
    hideOffer: hideOfferReducer,
    bookedOfferId: (state: IState = preloadedState) => state,
    btiErrorData: BtiDataErrorPopupReducer,
    captchaSiteKey: (state: IState = preloadedState) => state,
    captchaSpamSiteKey: (state: IState = preloadedState) => state,
    commentsData: commentsReducer,
    complaint: complaintReducer,
    contractRentSale: contractRentSaleReducer,
    demand: demandReducer,
    requestDemand: requestDemandReducer,
    verifyPhone: verifyPhoneReducer,
    experiments: experimentsReducer,
    favoriteMotivation: favoriteMotivationReducer,
    feedback: feedbackReducer,
    gallery: galleryReducer,
    isPikBookingSucceeded: (state: IState = preloadedState) => state,
    legend: legendModalReducer,
    mapSimilar: mapSimilarReducer,
    mapTabs: mapTabsReducer,
    mortgageMeta: mortgageMetaReducer,
    newbuildingChat: newbuildingChatReducer,
    newbuildingConsultant: (state: IState = preloadedState) => state,
    newbuildingMvk: newbuildingMvkReducer,
    offerChat: chatReducer,
    offerData: reduceReducers<IGetOfferDataResponseSchema>(offerReducer, userDataReducer),
    offerStatsPopup: offerStatsPopupReducer,
    pageData: (state: IState = preloadedState) => state,
    phoneCollapse: phoneCollapseReducer,
    popupCallback: popupCallbackReducer,
    printModal: printModalReducer,
    pushSubscribed: pushSubscribedReducer,
    reclaimModal: reclaimModalReducer,
    remove: removeModalReducer,
    reviewRequest: reviewRequestReducer,
    seoLinks: seoLinksReducer,
    serviceBaseUrl: (state: IState = preloadedState) => state,
    similarOffers: similarOffersReducer,
    specialPromoInfo: specialPromoInfoReducer,
    subscriptionPriceChangesPopup: subscriptionPriceChangesPopupReducer,
    tracking: trackingReducer,
    isStalkerBannerShown: stalkerBanner,
    viewedSimilar: markViewedReducer,
    ebcInfo: ebcReducer,
    complaintFeedbackFrom: complaintFeedbackReducer,
    mlSearchRegions: (state: IState = preloadedState) => state,
    isMlSearchForAllEnabled: (state: IState = preloadedState) => state,
    isBrandingAvailable: (state: IState = preloadedState) => state,
    isPravocardEnabled: (state: IState = preloadedState) => state,
    isCountrysideOffersBuilderBadgeEnabled: createSimpleReducer(preloadedState),
    countrysideBuilderLandingData: createSimpleReducer(preloadedState),
    isHomeowner: homeownerReducer,
    subscriptionAddress: subscriptionAddressReducer,
    akkCheckPassedOfferIds: createSimpleReducer(preloadedState),
    soprApi: (state: IState = preloadedState) => state,
    widgetsMarkup: widgetMarkupReducer,
    infrastructureMap: infrastructureMapReducer,
    isCountrysidePromoEnabled: (state: IState = preloadedState) => state,
    isProjectListEnabled: createSimpleReducer(preloadedState),
    isQuizEnabled: (state: IState = preloadedState) => state,
    knAuctionCompanies: (state: IState = preloadedState) => state,
    dealRent,
    complaintPopupOpen: complaintPopupOpenReducer,
    excludedUtilitiesTermsRegions: (state: IState = preloadedState) => state,
    dealRentVasCost: (state: IState = preloadedState) => state,
    newbuildingLabel: (state: IState = preloadedState) => state,
    isHomeownerDocsRequired: (state: IState = preloadedState) => state,
    offerHistory: offerHistoryReducer,
    showAnalyticsButton: (state: IState = preloadedState) => state,
    commercialSurvey: commercialSurveyWidgetModal,
    entryToShowEnabled: (state: IState = preloadedState) => state,
  });

  // tslint:disable-next-line:strict-type-predicates
  const composeEnhancers = (typeof window !== 'undefined' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) || compose;

  const createReduxStore = () =>
    _createStore(
      rootReducer,
      preloadedState,
      composeEnhancers(applyMiddleware(thunkMiddleware.withExtraArgument(context))),
    );

  if (process.env.NODE_ENV === 'development') {
    if (isBrowser) {
      if (!storeReminder) {
        storeReminder = createReduxStore();

        return storeReminder;
      }

      storeReminder.replaceReducer(rootReducer);

      return storeReminder;
    }
  }

  return createReduxStore();
}

function reduceReducers<S>(...reducers: Reducer<S>[]) {
  return (prevState: S, action: AnyAction) =>
    reducers.reduce((newState, reducer) => reducer(newState, action), prevState);
}
